import { Content } from '../../libs/gs-component-right-rail';
import pluralize from 'pluralize';
import React, { useState } from 'react';
import Icon from '@xo-union/tk-component-icons';
import LinkButton from '../LinkButton';

import { findLeader, SplitHouseholds } from '../../helpers/households';
import { Household } from './AddGuestRail';
import HouseholdRow from './HouseholdRow';
import SelectAllCheckbox from './SelectAllCheckbox';
import styles from './rightRails.scss';

interface Props {
  households: SplitHouseholds;
  onClickBulkEdit: (action: string) => void;
  setGuestRailOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedHouseholds: Set<string>;
  setSelectedHouseholds: React.Dispatch<React.SetStateAction<Set<string>>>;
  setEditHousehold: Function;
  setHouseholdsList: Function;
  type: 'email' | 'phone';
}

const HouseholdRailContent: React.FC<Props> = ({
  households,
  setGuestRailOpen,
  onClickBulkEdit,
  selectedHouseholds,
  setSelectedHouseholds,
  setEditHousehold,
  setHouseholdsList,
  type,
}) => {
  const isSelected = (household: Household) => {
    const leaderId = findLeader(household)?.id || '';

    return !!selectedHouseholds.has(leaderId);
  };

  const onSelect = (e: React.FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;

    if (selectedHouseholds.has(value)) {
      selectedHouseholds.delete(value);
      setSelectedHouseholds(new Set(selectedHouseholds));
    } else {
      setSelectedHouseholds(new Set(selectedHouseholds.add(value)));
    }
  };

  const unContactable = households.unContactable.length;
  const contactableLength = households.contactable.length;
  const optedOutLength = households.optedOut.length;
  const [optedOutExpanded, setOptedOutExpanded] = useState(false);

  return (
    <Content className={styles.householdsContainer}>
      {unContactable > 0 ? (
        <div className={styles.householdList}>
          <LinkButton
            className={styles.uncontactableHeading}
            onClick={() => {
              const action = type === 'email' ? 'bulk update email initiated' : 'bulk update phone number initiated';
              onClickBulkEdit(action);
              setGuestRailOpen(true);
              setHouseholdsList(households.unContactable);
            }}
          >
            <div className={`${styles.householdHeader} ${styles.unContactable}`}>
              <div className={styles.householdLabel} data-testid="bulkedit-label">
                Add
                {type === 'email'
                  ? `${unContactable === 1 ? ' an' : ''} email ${pluralize('address', unContactable)}`
                  : `${unContactable === 1 ? ' a' : ''} phone ${pluralize('number', unContactable)}`}{' '}
                for{' '}
                <span>
                  {unContactable} {pluralize('guests', unContactable)}
                </span>
              </div>
            </div>
          </LinkButton>
        </div>
      ) : null}

      {contactableLength > 0 ? (
        <div className={styles.householdList}>
          <div className={styles.householdHeader}>
            <div className={styles.labelWithCheckbox}>
              <div className={styles.selectAll}>
                <SelectAllCheckbox
                  households={households}
                  selectedHouseholds={selectedHouseholds}
                  setSelectedHouseholds={setSelectedHouseholds}
                />
              </div>

              <div className={styles.householdLabel}>
                Select {contactableLength > 1 ? 'all' : ''} {contactableLength} {pluralize('guest', contactableLength)}{' '}
                with{' '}
                {type === 'email'
                  ? `email ${pluralize('address', contactableLength)}`
                  : `phone ${pluralize('number', contactableLength)}`}
              </div>
            </div>
          </div>
          <div className={styles.householdRows}>
            {households.contactable.map(household => (
              <HouseholdRow
                household={household}
                onSelect={onSelect}
                isSelected={isSelected(household)}
                key={household.id}
                validateType={type}
              />
            ))}
          </div>
        </div>
      ) : null}
      {optedOutLength > 0 ? (
        <div className={styles.householdList}>
          <div className={styles.householdHeader}>
            <div className={styles.householdLabel}>
              {optedOutLength} {pluralize('guest', optedOutLength)} unsubscribed
            </div>
            <LinkButton
              className={styles.expandIcon}
              onClick={() => {
                setOptedOutExpanded(!optedOutExpanded);
              }}
            >
              <Icon name={optedOutExpanded ? 'caret_up' : 'caret_down'} size="md" className={styles.icon} />
            </LinkButton>
          </div>

          {optedOutExpanded ? (
            <div className={styles.householdRows}>
              {households.optedOut.map(household => (
                <HouseholdRow
                  household={household}
                  setGuestRailOpen={setGuestRailOpen}
                  setEditHousehold={setEditHousehold}
                  key={household.id}
                  validateType={type}
                  disableCheckbox={true}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </Content>
  );
};

export default HouseholdRailContent;
